/**
 * MarketIntelPricingPage — /omega/market-intel/pricing
 *
 * Pricing benchmark viewer for the Market Intelligence Brain (Phase 40 V-025).
 * Fetches /api/v1/market-intel/pricing?category={category}.
 */

import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { apiFetch } from '@/api/http';

const CATEGORIES = [
  'Financial',
  'HealthAndWellbeing',
  'TechnologyAndInfrastructure',
  'WorkAndProductivity',
];

interface PricingBenchmark {
  benchmark_id: string;
  category: string;
  price_range_low_usd: number;
  price_range_high_usd: number;
  median_price_usd: number;
  // F-10: cents companions. Prefer these when present.
  price_range_low_usd_cents?: number;
  price_range_high_usd_cents?: number;
  median_price_usd_cents?: number;
  pricing_model: string;
  sample_count: number;
  confidence: string;
  sources_json: string;
  created_at: number;
}

interface PricingResponse {
  benchmark: PricingBenchmark | null;
}

export function MarketIntelPricingPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const [category, setCategory] = useState('Financial');

  const { data, isLoading, error } = useQuery({
    queryKey: ['market-intel-pricing', category],
    queryFn: () => apiFetch<PricingResponse>(`/market-intel/pricing?category=${category}`),
  });

  const bm = data?.benchmark;

  return (
    <div className="omega-page" style={{ padding: '16px 20px' }}>
      <Breadcrumbs
        crumbs={[
          { label: 'OMEGA', to: '/omega' },
          { label: 'Market Intel', to: '/omega/market-intel' },
          { label: 'Pricing' },
        ]}
      />

      <h1 style={{ fontSize: '12pt', fontWeight: 700, margin: '12px 0 16px' }}>
        Pricing Benchmarks
      </h1>

      <div style={{ marginBottom: 16 }}>
        <label style={{ fontSize: '9pt', marginRight: 8 }}>Category:</label>
        <select
          value={category}
          onChange={e => setCategory(e.target.value)}
          style={{ fontSize: '9pt', padding: '4px 8px' }}
        >
          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {isLoading && <p style={{ fontSize: '9pt' }}>Loading...</p>}
      {error && <p style={{ fontSize: '9pt', color: '#f87171' }}>Error loading benchmark.</p>}
      {!isLoading && !bm && (
        <p style={{ fontSize: '9pt', color: 'var(--omega-ink-muted)' }}>No benchmark yet for {category}.</p>
      )}

      {bm && (
        <table style={{ borderCollapse: 'collapse', fontSize: '8.5pt' }}>
          <tbody>
            {[
              // F-10: prefer _cents fields when available (i64 at boundary avoids f64 rounding).
              ['Price range (low)', `$${(bm.price_range_low_usd_cents != null ? bm.price_range_low_usd_cents / 100 : bm.price_range_low_usd).toFixed(2)}`],
              ['Price range (high)', `$${(bm.price_range_high_usd_cents != null ? bm.price_range_high_usd_cents / 100 : bm.price_range_high_usd).toFixed(2)}`],
              ['Median price', `$${(bm.median_price_usd_cents != null ? bm.median_price_usd_cents / 100 : bm.median_price_usd).toFixed(2)}`],
              ['Pricing model', bm.pricing_model],
              ['Sample count', bm.sample_count],
              ['Confidence', bm.confidence],
              ['Updated', new Date(bm.created_at * 1000).toLocaleDateString()],
            ].map(([label, value]) => (
              <tr key={String(label)} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                <td style={{ padding: '6px 14px 6px 0', color: 'var(--omega-ink-muted)', minWidth: 160 }}>{label}</td>
                <td style={{ padding: '6px 0', fontWeight: 600 }}>{value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
